<template>
  <header class="top-header">
    <div class="header-left">
      <button class="menu-toggle" @click="toggleMobileMenu">
        <span class="hamburger"></span>
      </button>
      <div class="breadcrumb">
        <div class="page-title">
          <component :is="'span'" class="page-icon" v-html="pageIcon"></component>
          <span class="page-name">{{ pageName }}</span>
        </div>
      </div>
    </div>

    <div class="header-right">
      <!-- Renewal Notification -->
      <div v-if="showRenewalNotification" class="renewal-notification" @click="navigateToPayments">
        <div class="renewal-badge">
          <span class="renewal-icon">⚡</span>
          <span class="renewal-text">Renewal Due</span>
        </div>
      </div>

      <!-- Credits Display (hidden for agents) -->
      <div v-if="!isAgent" class="credits-display">
        <div class="credits-container">
          <span class="coin-icon">🪙</span>
          <span class="credits-amount">{{ formattedCredits }}</span>
          <span class="credits-label">Credits</span>
        </div>
      </div>

      <!-- Dark Mode Toggle -->
      <button class="theme-toggle" @click="toggleTheme" :title="themeStore.isDark ? 'Switch to Light Mode' : 'Switch to Dark Mode'">
        <span class="theme-icon">{{ themeStore.isDark ? '☀️' : '🌙' }}</span>
      </button>

      <div class="user-menu" @click="toggleProfile">
        <div class="user-avatar">
          <img :src="`https://ui-avatars.com/api/?name=${userName}&background=667eea&color=fff`" alt="User" />
          <span class="user-status"></span>
        </div>
        <div class="user-info">
          <div class="user-name">{{ userName }}</div>
          <div class="user-role">{{ userRole }}</div>
        </div>
        <button class="user-dropdown">
          <span class="dropdown-icon">⌄</span>
        </button>
        <div v-if="showProfile" class="dropdown-menu">
          <button @click="handleLogout" class="dropdown-item logout-btn">
            <span class="dropdown-icon">🚪</span>
            Logout
          </button>
        </div>
      </div>
    </div>
  </header>
</template>

<script>
import { authStore } from '@/store/auth'
import { themeStore } from '@/store/theme'
import { PaymentsService } from '@/services/payments'

export default {
  name: 'TopHeader',
  props: {
    pageTitle: {
      type: String,
      default: null
    }
  },
  data () {
    return {
      showProfile: false,
      authStore, // Make authStore available to template
      themeStore, // Make themeStore available to template
      subscription: null,
      subscriptionLoading: true
    }
  },
  computed: {
    isAgent () {
      return !!localStorage.getItem('agent_token')
    },
    pageName () {
      // If pageTitle prop is provided, use it
      if (this.pageTitle) {
        return this.pageTitle
      }

      // Otherwise, determine from route name
      const routeName = this.$route.name
      const pageNames = {
        dashboard: 'Dashboard',
        agents: 'Agents Management',
        departments: 'Department Management',
        messages: 'Team Messaging',
        whatsapp: 'WhatsApp',
        'whatsapp-broadcast': 'WhatsApp Broadcast',
        'whatsapp-templates': 'WhatsApp Templates',
        'company-profile': 'Company Profile',
        'company-leads': 'Company Leads',
        todo: 'ToDo',
        customers: 'Customers',
        'products-services': 'Products/Services',
        appointments: 'Appointments System',
        'whatsapp-settings': 'WhatsApp Settings',
        leads: 'Leads Center',
        payments: 'Payments & Billing',
        'restaurant-settings': 'Restaurant Settings',
        'restaurant-categories': 'Menu Categories',
        'restaurant-menu-items': 'Menu Items',
        'restaurant-orders': 'Orders Management'
      }
      return pageNames[routeName] || 'Dashboard'
    },
    pageIcon () {
      const routeName = this.$route.name
      const pageIcons = {
        dashboard: '<svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M3 13h8V3H3v10zm0 8h8v-6H3v6zm10 0h8V11h-8v10zm0-18v6h8V3h-8z"/></svg>',
        agents: '<svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M16 4c0-1.11.89-2 2-2s2 .89 2 2-.89 2-2 2-2-.89-2-2zM4 18v-4h3v4H4zM6 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm1 3c-1.1 0-2 .9-2 2v5h2v-5c0-.55.45-1 1-1h4.5c.28 0 .5-.22.5-.5s-.22-.5-.5-.5H7zm11.5 4.5c.83 0 1.5.67 1.5 1.5s-.67 1.5-1.5 1.5S17 17.83 17 17s.67-1.5 1.5-1.5zM20 8.5V7c0-.55-.45-1-1-1h-2c-.55 0-1 .45-1 1v1.5c0 .28.22.5.5.5h3c.28 0 .5-.22.5-.5zM12 14c.55 0 1-.45 1-1V9c0-.55-.45-1-1-1s-1 .45-1 1v4c0 .55.45 1 1 1z"/></svg>',
        departments: '<svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M12 7V3H2v18h20V7H12zM6 19H4v-2h2v2zm0-4H4v-2h2v2zm0-4H4V9h2v2zm0-4H4V5h2v2zm4 12H8v-2h2v2zm0-4H8v-2h2v2zm0-4H8V9h2v2zm0-4H8V5h2v2zm10 12h-8v-2h2v-2h-2v-2h2v-2h-2V9h8v10zm-2-8h-2v2h2v-2zm0 4h-2v2h2v-2z"/></svg>',
        messages: '<svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M20 2H4c-1.1 0-1.99.9-1.99 2L2 22l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zM6 9h12v2H6V9zm8 5H6v-2h8v2zm4-6H6V6h12v2z"/></svg>',
        whatsapp: '<svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.56-.01-.188 0-.495.074-.754.372-.26.297-.99.967-.99 2.357 0 1.388 1.014 2.731 1.155 2.921.142.19 2.006 3.045 4.848 4.272.678.292 1.207.466 1.621.596.682.217 1.303.187 1.793.113.547-.081 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347zm-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.051 3.488z"/></svg>',
        'whatsapp-broadcast': '<svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M9.01 14H2v2h7.01v3L13 15l-3.99-4v3zm5.98-1v-3H22V8h-7.01V5L11 9l3.99 4z"/></svg>',
        'whatsapp-templates': '<svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M19,3H5C3.89,3 3,3.89 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V5C21,3.89 20.1,3 19,3M19,19H5V5H19V19M17,9.5V7.5H7V9.5H17M14,13.5V11.5H7V13.5H14M17,17.5V15.5H7V17.5H17Z"/></svg>',
        'company-profile': '<svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C13.1 2 14 2.9 14 4C14 5.1 13.1 6 12 6C10.9 6 10 5.1 10 4C10 2.9 10.9 2 12 2ZM21 9V7L15 1H5C3.89 1 3 1.89 3 3V18A2 2 0 0 0 5 20H19A2 2 0 0 0 21 18V9M19 18H5V3H13V9H19Z"/></svg>',
        'company-leads': '<svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M16 4c0-1.11.89-2 2-2s2 .89 2 2-.89 2-2 2-2-.89-2-2zM4 18v-4h3v4H4zM6 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm1 3c-1.1 0-2 .9-2 2v5h2v-5c0-.55.45-1 1-1h4.5c.28 0 .5-.22.5-.5s-.22-.5-.5-.5H7zm11.5 4.5c.83 0 1.5.67 1.5 1.5s-.67 1.5-1.5 1.5S17 17.83 17 17s.67-1.5 1.5-1.5zM20 8.5V7c0-.55-.45-1-1-1h-2c-.55 0-1 .45-1 1v1.5c0 .28.22.5.5.5h3c.28 0 .5-.22.5-.5zM12 14c.55 0 1-.45 1-1V9c0-.55-.45-1-1-1s-1 .45-1 1v4c0 .55.45 1 1 1z"/></svg>',
        todo: '<svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>',
        customers: '<svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M16 4c0-1.11.89-2 2-2s2 .89 2 2-.89 2-2 2-2-.89-2-2zM4 18v-4h3v4H4zM13 12.5h3.5l-1-4.5h-2.5v4.5zM12 13c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm0-8c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3z"/></svg>',
        'products-services': '<svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M12,2A3,3 0 0,1 15,5V11A3,3 0 0,1 12,14A3,3 0 0,1 9,11V5A3,3 0 0,1 12,2M19,11C19,14.53 16.39,17.44 13,17.93V21H11V17.93C7.61,17.44 5,14.53 5,11H7A5,5 0 0,0 12,16A5,5 0 0,0 17,11H19Z"/></svg>',
        appointments: '<svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M19,3H18V1H16V3H8V1H6V3H5A2,2 0 0,0 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V5A2,2 0 0,0 19,3M19,19H5V8H19V19M5,6V5H19V6H5Z"/></svg>',
        'whatsapp-settings': '<svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M12,2A10,10 0 0,1 22,12A10,10 0 0,1 12,22C9.84,22 7.87,21.19 6.34,19.78L2,22L4.5,17.75C3.18,15.96 2.5,13.83 2.5,11.5C2.5,6.26 6.76,2 12,2M17.472,14.382C17.175,14.233 15.714,13.515 15.442,13.415C15.17,13.316 14.972,13.267 14.772,13.565C14.575,13.862 14.005,14.531 13.832,14.729C13.659,14.928 13.486,14.952 13.189,14.803C12.892,14.653 11.934,14.34 10.799,13.328C9.916,12.54 9.319,11.567 9.146,11.269C8.973,10.972 9.128,10.814 9.276,10.666C9.41,10.533 9.574,10.319 9.722,10.146C9.871,9.972 9.92,9.848 10.02,9.65C10.119,9.452 10.07,9.279 9.995,9.13C9.92,8.981 9.326,7.518 9.079,6.923C8.837,6.344 8.592,6.424 8.423,6.414C8.263,6.407 8.064,6.405 7.866,6.405C7.668,6.405 7.371,6.479 7.112,6.777C6.854,7.074 6.124,7.744 6.124,9.207C6.124,10.67 7.138,12.062 7.288,12.26C7.437,12.458 9.443,15.503 12.291,16.775C12.969,17.067 13.496,17.241 13.917,17.337C14.599,17.554 15.202,17.521 15.692,17.447C16.239,17.366 17.472,16.708 17.72,15.994C17.968,15.28 17.968,14.651 17.893,14.527C17.818,14.403 17.62,14.328 17.322,14.179L17.472,14.382Z"/></svg>',
        leads: '<svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M16,6L18.29,8.29L13.41,13.17L9.41,9.17L2,16.59L3.41,18L9.41,12L13.41,16L19.71,9.71L22,12V6H16Z"/></svg>',
        payments: '<svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M20,4H4C2.89,4 2,4.89 2,6V18A2,2 0 0,0 4,20H20A2,2 0 0,0 22,18V6C22,4.89 21.11,4 20,4M20,18H4V12H20V18M20,6V10H4V6H20M6,16A1,1 0 0,1 7,17A1,1 0 0,1 6,18A1,1 0 0,1 5,17A1,1 0 0,1 6,16Z"/></svg>',
        'restaurant-settings': '<svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M12,15.5A3.5,3.5 0 0,1 8.5,12A3.5,3.5 0 0,1 12,8.5A3.5,3.5 0 0,1 15.5,12A3.5,3.5 0 0,1 12,15.5M19.43,12.97C19.47,12.65 19.5,12.33 19.5,12C19.5,11.67 19.47,11.34 19.43,11L21.54,9.37C21.73,9.22 21.78,8.95 21.66,8.73L19.66,5.27C19.54,5.05 19.27,4.96 19.05,5.05L16.56,6.05C16.04,5.66 15.5,5.32 14.87,5.07L14.5,2.42C14.46,2.18 14.25,2 14,2H10C9.75,2 9.54,2.18 9.5,2.42L9.13,5.07C8.5,5.32 7.96,5.66 7.44,6.05L4.95,5.05C4.73,4.96 4.46,5.05 4.34,5.27L2.34,8.73C2.22,8.95 2.27,9.22 2.46,9.37L4.57,11C4.53,11.34 4.5,11.67 4.5,12C4.5,12.33 4.53,12.65 4.57,12.97L2.46,14.63C2.27,14.78 2.22,15.05 2.34,15.27L4.34,18.73C4.46,18.95 4.73,19.03 4.95,18.95L7.44,17.94C7.96,18.34 8.5,18.68 9.13,18.93L9.5,21.58C9.54,21.82 9.75,22 10,22H14C14.25,22 14.46,21.82 14.5,21.58L14.87,18.93C15.5,18.68 16.04,18.34 16.56,17.94L19.05,18.95C19.27,19.03 19.54,18.95 19.66,18.73L21.66,15.27C21.78,15.05 21.73,14.78 21.54,14.63L19.43,12.97Z"/></svg>',
        'restaurant-categories': '<svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M16,6V8H14V13H16V20H6V13H8V8H6V6H16M18,2H6A2,2 0 0,0 4,4V22H8V14H16V22H20V4A2,2 0 0,0 18,2Z"/></svg>',
        'restaurant-menu-items': '<svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M11,9H9V2H7V9H5V2H3V9C3,11.12 4.66,12.84 6.75,12.97V22H9.25V12.97C11.34,12.84 13,11.12 13,9V2H11V9M16,6V14H18.5V22H21V2C18.24,2 16,4.24 16,6Z"/></svg>',
        'restaurant-orders': '<svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M19,3H5C3.89,3 3,3.89 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V5C21,3.89 20.1,3 19,3M9,17H7V10H9V17M13,17H11V7H13V17M17,17H15V13H17V17Z"/></svg>'
      }
      return pageIcons[routeName] || pageIcons.dashboard
    },
    userName () {
      // For agents, get name from agent_data
      if (this.isAgent) {
        const agentData = JSON.parse(localStorage.getItem('agent_data') || '{}')
        return agentData.name || 'Agent'
      }
      // For regular users, get from authStore
      return (authStore.user && authStore.user.name) || 'User'
    },
    userRole () {
      // For agents, show department and role
      if (this.isAgent) {
        const agentData = JSON.parse(localStorage.getItem('agent_data') || '{}')
        const department = agentData.department_name || ''
        const role = agentData.agent_role_name || ''
        if (department && role) {
          return `${department} • ${role}`
        }
        return department || role || 'Agent'
      }
      // For regular users, could show company role here
      return 'Admin'
    },
    formattedCredits () {
      if (authStore.creditsLoading) {
        return 'Loading...'
      }
      // Format large numbers with commas
      return authStore.credits.toLocaleString()
    },
    showRenewalNotification () {
      if (!this.subscription || this.subscriptionLoading) return false
      return this.subscription.is_time_to_renew || this.subscription.days_to_renewal <= 7
    }
  },
  async mounted () {
    // Load credits when component mounts if user is authenticated
    if (authStore.isAuthenticated && !authStore.creditsLoading) {
      await authStore.loadCredits()
    }

    // Load subscription status for renewal notification
    if (authStore.isAuthenticated) {
      await this.loadSubscriptionStatus()
    }
  },
  methods: {
    toggleMobileMenu () {
      // Dispatch custom event to toggle mobile menu
      window.dispatchEvent(new CustomEvent('toggleMobileMenu'))
    },
    toggleProfile () {
      this.showProfile = !this.showProfile
    },
    toggleTheme () {
      themeStore.toggleDarkMode()
    },
    handleLogout () {
      // Close dropdown
      this.showProfile = false

      if (this.isAgent) {
        console.log('Agent logout clicked from TopHeader - clearing agent data...')

        // Clear agent data and auth token
        localStorage.removeItem('agent_token')
        localStorage.removeItem('agent_data')
        localStorage.removeItem('auth_token')

        console.log('Agent storage cleared, redirecting to agent login...')

        // Navigate to agent login
        this.$router.push('/agent-login')
      } else {
        console.log('Owner logout clicked from TopHeader - clearing user data...')

        // Force clear localStorage directly
        localStorage.removeItem('auth_token')
        localStorage.removeItem('user_data')
        localStorage.removeItem('token')
        localStorage.removeItem('user')

        // Clear auth store
        authStore.logout()

        console.log('Storage cleared, redirecting to login...')

        // Navigate to login
        this.$router.push('/login')
      }
    },
    // Emergency clear method you can call from console
    emergencyClear () {
      localStorage.clear()
      location.reload()
    },
    // Force logout when user data is corrupted
    forceLogout () {
      console.log('Force logout clicked - emergency cleanup...')

      // Clear all storage
      localStorage.clear()
      sessionStorage.clear()

      // Reset auth store to initial state
      authStore.user = null
      authStore.isAuthenticated = false
      authStore.credits = 0

      console.log('Emergency cleanup completed, reloading page...')

      // Reload page to reset everything
      location.reload()
    },
    async loadSubscriptionStatus () {
      this.subscriptionLoading = true
      try {
        const result = await PaymentsService.getSubscriptionStatus()
        if (result.success) {
          this.subscription = result.subscription
        }
      } catch (error) {
        console.error('Error loading subscription status:', error)
      } finally {
        this.subscriptionLoading = false
      }
    },
    navigateToPayments () {
      this.$router.push('/payments')
    }
  }
}
</script>

<style scoped>
.top-header {
  height: 72px;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-bottom: 1px solid var(--border-primary);
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 32px;
  color: var(--text-primary);
}

.dark .top-header {
  background: rgba(17, 24, 39, 0.95);
}

.top-header {
  position: sticky;
  top: 0;
  z-index: 900;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 20px;
}

.menu-toggle {
  display: none;
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
}

.hamburger {
  width: 20px;
  height: 2px;
  background: #374151;
  display: block;
  position: relative;
}

.hamburger::before,
.hamburger::after {
  content: '';
  position: absolute;
  width: 20px;
  height: 2px;
  background: #374151;
  transition: all 0.3s;
}

.hamburger::before {
  top: -6px;
}

.hamburger::after {
  top: 6px;
}

.breadcrumb {
  display: flex;
  align-items: center;
  gap: 8px;
}

.page-title {
  display: flex;
  align-items: center;
  gap: 12px;
}

.page-icon {
  color: #3b82f6;
  display: flex;
  align-items: center;
}

.page-name {
  font-size: 18px;
  font-weight: 600;
  color: var(--text-primary);
}

.breadcrumb-item {
  font-size: 14px;
  color: var(--text-secondary);
}

.breadcrumb-item.current {
  color: var(--text-primary);
  font-weight: 600;
}

.breadcrumb-separator {
  color: var(--text-tertiary);
}

.header-center {
  flex: 1;
  max-width: 500px;
  margin: 0 32px;
}

.search-box {
  position: relative;
  display: flex;
  align-items: center;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 0 16px;
  transition: all 0.2s;
}

.search-box:focus-within {
  background: white;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.search-icon {
  font-size: 16px;
  margin-right: 12px;
  opacity: 0.5;
}

.search-input {
  flex: 1;
  border: none;
  background: none;
  padding: 12px 0;
  font-size: 14px;
  color: var(--text-primary);
  outline: none;
}

.search-input::placeholder {
  color: var(--text-tertiary);
}

.search-shortcut {
  font-size: 12px;
  color: var(--text-tertiary);
  background: var(--bg-tertiary);
  padding: 2px 6px;
  border-radius: 4px;
  margin-left: 12px;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 24px;
}

.credits-display {
  margin-right: 16px;
}

.credits-container {
  display: flex;
  align-items: center;
  gap: 8px;
  background: var(--bg-secondary);
  color: var(--text-primary);
  padding: 6px 12px;
  border-radius: 8px;
  font-weight: 500;
  border: 1px solid var(--border-primary);
  transition: all 0.2s;
}

.credits-container:hover {
  background: var(--bg-tertiary);
  border-color: var(--border-secondary);
}

.coin-icon {
  font-size: 14px;
  opacity: 0.8;
}

.credits-amount {
  font-size: 14px;
  font-weight: 600;
}

.credits-label {
  font-size: 12px;
  opacity: 0.7;
  font-weight: 400;
}

/* Theme Toggle */
.theme-toggle {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: none;
  background: var(--bg-tertiary);
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-right: 12px;
}

.theme-toggle:hover {
  background: var(--border-secondary);
  transform: scale(1.05);
}

.theme-icon {
  font-size: 18px;
  transition: transform 0.2s ease;
}

.theme-toggle:hover .theme-icon {
  transform: rotate(10deg);
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.action-btn {
  position: relative;
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  border-radius: 8px;
  transition: all 0.2s;
}

.action-btn:hover {
  background: #f3f4f6;
}

.action-icon {
  font-size: 18px;
  opacity: 0.7;
}

.notification-dot {
  position: absolute;
  top: 6px;
  right: 6px;
  width: 8px;
  height: 8px;
  background: #ef4444;
  border-radius: 50%;
  border: 2px solid white;
}

.notification-count {
  position: absolute;
  top: 2px;
  right: 2px;
  background: #ef4444;
  color: white;
  font-size: 10px;
  font-weight: 600;
  padding: 1px 4px;
  border-radius: 8px;
  min-width: 16px;
  text-align: center;
}

.user-menu {
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  padding: 8px 12px;
  border-radius: 12px;
  transition: all 0.2s;
}

.user-menu:hover {
  background: var(--bg-secondary);
}

.user-avatar {
  position: relative;
}

.user-avatar img {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 2px solid #e5e7eb;
}

.user-status {
  position: absolute;
  bottom: 0;
  right: 0;
  width: 12px;
  height: 12px;
  background: #10b981;
  border: 2px solid white;
  border-radius: 50%;
}

.user-info {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.user-name {
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
  line-height: 1.2;
}

.user-role {
  font-size: 12px;
  color: var(--text-secondary);
  line-height: 1.2;
  margin-top: 2px;
}

.user-dropdown {
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
}

.dropdown-icon {
  font-size: 12px;
  color: var(--text-tertiary);
}

.user-menu {
  position: relative;
}

/* Fallback user menu styles */
.user-menu-fallback {
  display: flex;
  align-items: center;
}

.emergency-logout-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  border: none;
  border-radius: 8px;
  color: white;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 2px 8px rgba(239, 68, 68, 0.2);
}

.emergency-logout-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3);
  background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
}

.logout-icon {
  font-size: 16px;
}

.logout-text {
  font-weight: 600;
}

.dropdown-menu {
  position: absolute;
  top: 100%;
  right: 0;
  background: var(--bg-primary);
  border: 1px solid var(--border-primary);
  border-radius: 8px;
  box-shadow: 0 10px 25px var(--shadow-primary);
  min-width: 240px;
  transition: background-color 0.3s ease, border-color 0.3s ease;
  z-index: 1000;
  margin-top: 8px;
}

.dropdown-item {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 12px 16px;
  text-decoration: none;
  color: var(--text-primary);
  border: none;
  transition: background-color 0.2s, color 0.2s;
  background: none;
  text-align: left;
  cursor: pointer;
  font-size: 14px;
  transition: background 0.2s;
}

.dropdown-item .dropdown-icon {
  font-size: 16px;
  opacity: 0.7;
}

.dropdown-item:hover {
  background: var(--bg-secondary);
}

.logout-btn {
  color: #dc2626;
}

.logout-btn:hover {
  background: var(--bg-secondary);
  color: #ef4444;
}

@media (max-width: 768px) {
  .top-header {
    position: fixed;
    width: 100%;
    top: 0;
    z-index: 1500;
    padding: 0 16px;
    height: 64px;
    background: rgba(255, 255, 255, 0.98);
    backdrop-filter: blur(20px);
  }

  .menu-toggle {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 10px;
    border-radius: 8px;
  }

  .menu-toggle:hover {
    background: #f3f4f6;
  }

  .hamburger,
  .hamburger::before,
  .hamburger::after {
    transition: all 0.3s ease;
  }

  .header-left {
    gap: 12px;
  }

  .breadcrumb-item {
    font-size: 16px;
    font-weight: 600;
  }

  .header-center {
    display: none;
  }

  .user-info {
    display: none;
  }

  .credits-display {
    display: none; /* Hide credits on mobile */
  }

  .user-avatar img {
    width: 36px;
    height: 36px;
  }

  .dropdown-menu {
    right: 8px;
    margin-top: 4px;
    min-width: 200px;
  }
}

/* Renewal Notification Styles */
.renewal-notification {
  margin-right: 16px;
}

.renewal-badge {
  display: flex;
  align-items: center;
  gap: 6px;
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  color: white;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 2px 8px rgba(239, 68, 68, 0.25);
  animation: pulse 2s infinite;
}

.renewal-badge:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.35);
  background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
}

.renewal-icon {
  font-size: 14px;
  animation: flash 1.5s infinite;
}

.renewal-text {
  font-weight: 600;
  letter-spacing: 0.3px;
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.8; }
}

@keyframes flash {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.6; }
}

@media (max-width: 768px) {
  .renewal-notification {
    display: none; /* Hide on mobile to save space */
  }
}
</style>
